import { Bar, BarChart, CartesianGrid, Cell, LabelList, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEffect, useState } from "react";
import { Card, NdBadge } from "@/components/Kpi";
import { eurShort, num } from "@/lib/format";
import { PALETTE, usePalette } from "@/lib/palette";

const COLORS = { curr: "#0EA5E9", prev: "#94A3B8", pos: "#10B981", warn: "#F59E0B", grigio: "#94A3B8",
  get caricato() { return PALETTE.caricato; },
  get liquidato() { return PALETTE.liquidato; },
  get liq() { return PALETTE.liquidato; } };
const PIE_COLORS = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#A855F7", "#EF4444", "#14B8A6", "#64748B", "#DB2777"];

const compact = (v) => {
  if (v === null || v === undefined) return "";
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toLocaleString("it-IT", { maximumFractionDigits: 2 })}M`;
  if (a >= 1e4) return `${(v / 1e3).toLocaleString("it-IT", { maximumFractionDigits: 0 })}K`;
  return num(v, 0);
};

const tip = {
  contentStyle: { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "JetBrains Mono, monospace" },
  formatter: (v) => num(v, 0),
};

/** true sotto i 640px: usato per ridurre l'altezza dei grafici su mobile. */
export function useMobile() {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

const labelStyle = { fontSize: 11, fill: "#0F172A", fontWeight: 600,
  fontFamily: "JetBrains Mono, monospace" };
const labelStyleDesktop = { fontSize: 10, fill: "#475569", fontFamily: "JetBrains Mono, monospace" };

/** Colore dello scostamento: verde se positivo, giallo fino a -10%, rosso oltre. */
const coloreScostamento = (v) => (v === null || v === undefined ? COLORS.grigio
  : v >= 0 ? COLORS.pos : v >= -10 ? COLORS.warn : "#DC2626");

export function ComparisonChart({ title, data, keys, type = "bar", height = 280, testId, emptyNote, hideLabels, labelFormat = "compact", onBarClick, coloreDalSegno, orizzontale, legenda, larghezzaEtichette = 34, tooltipExtra }) {
  // sui grafici di scostamento la scala parte sempre da +/-25%: barre piccole ben leggibili
  const dominioPerc = () => {
    const vals = (data || []).flatMap((r) => keys.map((k) => r[k.key]))
      .filter((v) => v !== null && v !== undefined);
    const est = Math.max(25, ...vals.map((v) => Math.abs(v)));
    const lim = Math.ceil(est / 5) * 5;
    return [-lim, lim];
  };
  const dominio = coloreDalSegno ? dominioPerc() : undefined;
  const hasData = data?.some((d) => keys.some((k) => d[k.key] !== null && d[k.key] !== 0 && d[k.key] !== undefined));
  const fmtLabel = labelFormat === "int" ? (v) => num(v, 0)
    : labelFormat === "perc" ? (v) => (v === null || v === undefined ? "" : `${num(v, 2)}%`)
    : compact;
  const mobile = useMobile();
  usePalette();
  const h = mobile ? Math.min(height, 220) : height;
  const [attivo, setAttivo] = useState(null);
  const stacked = keys.some((k) => k.stackId);
  // i valori non sono stampati sulle barre: compaiono solo sull'elemento cliccato
  const etichettaAttiva = (props) => {
    const { x, y, width, height: hb, value, index } = props;
    if (index !== attivo || value === null || value === undefined) return null;
    return orizzontale
      ? <text x={x + width + 4} y={y + hb / 2 + 3} textAnchor="start" style={labelStyle}>{fmtLabel(value)}</text>
      : <text x={x + width / 2} y={y - 5} textAnchor="middle" style={labelStyle}>{fmtLabel(value)}</text>;
  };
  const margin = orizzontale ? { top: 8, right: 46, left: 0, bottom: 0 }
    : { top: 18, right: 10, left: labelFormat === "compact" ? -14 : 22, bottom: 0 };
  return (
    <Card className="p-4 sm:p-6 h-full" data-testid={testId}>
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">{title}</div>
      {legenda && hasData ? (
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4"
          data-testid={testId ? `${testId}-legenda` : undefined}>
          {keys.map((k) => (
            <span key={k.key} className="flex items-center gap-2 text-xs text-slate-600">
              <span className="w-3 h-3 rounded-sm" style={{ background: k.color || COLORS.curr }} />
              {k.name}
            </span>
          ))}
        </div>
      ) : null}
      {!hasData ? (
        <div className="h-40 flex items-center justify-center"><NdBadge label={emptyNote || "dato non disponibile"} /></div>
      ) : (
        <ResponsiveContainer width="100%" height={h}>
          {type === "line" ? (
            <LineChart data={data} margin={margin}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={fmtLabel} />
              <Tooltip {...tip} formatter={(v, n, item) => {
                const base = labelFormat === "perc" ? fmtLabel(v) : num(v, 0);
                const extra = tooltipExtra ? tooltipExtra(item?.payload) : null;
                return extra ? `${base} · ${extra}` : base;
              }} />
              {keys.map((k) => (
                <Line key={k.key} type="monotone" dataKey={k.key} name={k.name}
                  stroke={k.color || COLORS.curr} strokeWidth={2} dot={{ r: 3 }}>
                  {!hideLabels && (mobile
                    ? null
                    : <LabelList dataKey={k.key} position="top" formatter={fmtLabel}
                        style={labelStyleDesktop} />)}
                </Line>
              ))}
            </LineChart>
          ) : (
            <BarChart data={data} margin={margin} layout={orizzontale ? "vertical" : "horizontal"}
              barCategoryGap={orizzontale ? undefined : "12%"} barGap={2}
              onClick={(e) => {
                const i = e?.activeTooltipIndex;
                setAttivo((v) => (v === i || i === undefined || i === null ? null : i));
                if (onBarClick && e?.activePayload?.[0]) onBarClick(e.activePayload[0].payload);
              }}
              style={{ cursor: "pointer" }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={!!orizzontale} horizontal={!orizzontale} />
              {orizzontale ? (
                <>
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={fmtLabel} />
                  <YAxis type="category" dataKey="label" width={larghezzaEtichette} tick={{ fontSize: 11, fill: "#64748B" }} interval={0} />
                </>
              ) : (
                <>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={fmtLabel}
                    domain={dominio} />
                </>
              )}
              <Tooltip {...tip} formatter={(v, n, item) => {
                const base = labelFormat === "perc" ? fmtLabel(v) : num(v, 0);
                const extra = tooltipExtra ? tooltipExtra(item?.payload) : null;
                return extra ? `${base} · ${extra}` : base;
              }} />
              {keys.map((k) => (
                <Bar key={k.key} dataKey={k.key} name={k.name} fill={k.color || COLORS.curr}
                  radius={orizzontale ? [0, 3, 3, 0] : [3, 3, 0, 0]} stackId={k.stackId}
                  cursor={onBarClick ? "pointer" : undefined}
                  onClick={(p, i) => {
                    setAttivo((v) => (v === i ? null : i));
                    if (onBarClick) onBarClick(p?.payload || p);
                  }}>
                  {coloreDalSegno && data.map((r) => (
                    <Cell key={r.label} fill={coloreScostamento(r[k.key])} />
                  ))}
                  {!coloreDalSegno && data.some((r) => r.colore) && data.map((r) => (
                    <Cell key={r.label} fill={r.colore || k.color || COLORS.curr} />
                  ))}
                  {!hideLabels && (mobile
                    ? <LabelList dataKey={k.key} content={etichettaAttiva} />
                    : <LabelList dataKey={k.key} formatter={fmtLabel}
                        style={k.labelStyle ? { ...labelStyleDesktop, ...k.labelStyle }
                          : labelStyleDesktop}
                        position={k.labelPosition
                          || (stacked ? "center" : orizzontale ? "right" : "top")} />)}
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
    </Card>
  );
}

export function PieBreakdown({ title, data, testId, valueKind = "eur", height = 300, emptyNote, minLabelPerc = 0, legendSide = false, senzaEtichette = false }) {
  // voci ordinate per incidenza decrescente, in torta e in legenda
  const voci = [...(data || [])].sort((a, b) => (b.value || 0) - (a.value || 0));
  const total = voci.reduce((s, d) => s + (d.value || 0), 0);
  const fmt = valueKind === "eur" ? eurShort : (v) => num(v, 0);
  const mobile = useMobile();
  const perc = (v) => num((v || 0) / total * 100, 1);
  // etichetta dentro la fetta: nome sulla prima riga, percentuale sulla seconda.
  // Restando all'interno del donut non viene mai tagliata dai bordi del box.
  const etichettaInterna = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
    const quota = (value || 0) / total * 100;
    if (quota < Math.max(minLabelPerc, 5)) return null;
    const rad = -midAngle * (Math.PI / 180);
    const r = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) / 2;
    const x = cx + r * Math.cos(rad);
    const y = cy + r * Math.sin(rad);
    const nome = String(name || "");
    return (
      <g>
        <text x={x} y={y - 4} textAnchor="middle" fill="#FFFFFF" fontSize={10} fontWeight={700}
          stroke="rgba(15,23,42,0.35)" strokeWidth={2} paintOrder="stroke">
          {nome.length > 14 ? `${nome.slice(0, 13)}…` : nome}
        </text>
        <text x={x} y={y + 8} textAnchor="middle" fill="#FFFFFF" fontSize={9.5} fontWeight={600}
          stroke="rgba(15,23,42,0.35)" strokeWidth={2} paintOrder="stroke">
          {`${perc(value)}%`}
        </text>
      </g>
    );
  };
  return (
    <Card className="p-4 sm:p-6 h-full" data-testid={testId}>
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">{title}</div>
      {!total ? (
        <div className="h-40 flex items-center justify-center"><NdBadge label={emptyNote || "dato non disponibile"} /></div>
      ) : (
        <div className={legendSide ? "flex flex-col lg:flex-row lg:items-center gap-6" : ""}>
          <div className={legendSide ? "lg:w-1/2" : ""}>
          <ResponsiveContainer width="100%" height={mobile ? Math.min(height, 220) : height}>
            <PieChart>
              <Pie data={voci} dataKey="value" nameKey="name" innerRadius="42%"
                outerRadius={mobile ? "88%" : "88%"} paddingAngle={2}
                label={senzaEtichette ? false : etichettaInterna} labelLine={false}>
                {voci.map((d, i) => <Cell key={d.name} fill={d.color || PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip {...tip} formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
          </div>
          <div className={legendSide
            ? "lg:w-1/2 space-y-2 text-sm"
            : "mt-2 space-y-1.5 text-xs"}>
            {voci.map((d, i) => (
              <div key={d.name} className="flex items-baseline gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full translate-y-[-1px]"
                  style={{ background: d.color || PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-slate-600 shrink-0">{d.name}</span>
                <span className="flex-1 border-b border-dotted border-slate-200" />
                <span className="font-mono whitespace-nowrap">{fmt(d.value)}</span>
                <span className="font-mono text-slate-400 whitespace-nowrap">{perc(d.value)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export { COLORS };
